import { ExistingCompany } from "../../types/addServiceTypes";
import { ServiceCapabilities, deriveServiceCapabilities } from "../../utils/serviceCapabilities";

export type ContractValidationType =
  | "own-both"
  | "own-injection"
  | "own-offtake"
  | "other-both"
  | "other-injection"
  | "other-offtake"
  | "customer-switch"
  | "customer-switch-blocked"
  | "warning"
  | "success";

export interface ContractValidationResult {
  canProceed: boolean;
  type: ContractValidationType;
  message: string;
  directions?: ("injection" | "offtake")[];
  companyName?: string;
  companyId?: number;
  activeContract?: string;
  disabledDirections: { injection: boolean; offtake: boolean };
  requiresUpdate: boolean;
  warning?: boolean;
  /**
   * Customer Switch (FT — admin-only). An admin is onboarding an EAN that is
   * active/future under ANOTHER company. Instead of the hard-stop shown to
   * non-admins, the admin proceeds and the point is onboarded programmatically
   * as a Customer Switch (switchType = "Customer Switch"). Only ever true when
   * isAdmin was passed AND the blocking contract belongs to another company.
   */
  customerSwitch?: boolean;
}

/**
 * FT — Customer Switch (admin-only). A point whose service components only offer
 * `SC_OFFINJE` can ONLY be contracted as a combined offtake+injection pair, so a
 * single-direction handover is structurally impossible. §5k (revised): flag and stop
 * rather than silently forcing both directions onto the new company — JediMaster has
 * never seen this shape in production, so we refuse instead of guessing.
 *
 * Pre-empts the backend, which rejects the same shape with 409
 * CUSTOMER_SWITCH_COMBINED_ONLY_PARTIAL. Stopping client-side avoids the doomed
 * round-trip and surfaces the actual reason.
 *
 * Single source of truth for this result — used by BOTH the partial-direction path
 * (`resolveAdminPartialSwitch`) and the `other-injection` / `other-offtake` admin
 * branches, which is where a genuinely `onlyOffinje` point actually lands (it fails
 * `hasAllThreeConfigs`, so it never enters that block).
 */
const buildCombinedOnlyBlocked = (
  row: ExistingCompany,
  direction: "injection" | "offtake",
  t?: (key: string) => string
): ContractValidationResult => ({
  canProceed: false,
  type: "customer-switch-blocked",
  message: t
    ? t("management:customerSwitch.blockedOnlyCombined")
    : "This connection point only supports a combined offtake & injection contract, but the current owner holds only one direction. This Customer Switch cannot be performed automatically.",
  directions: [direction],
  companyName: row.companyName,
  companyId: row.company_id,
  activeContract: row.activeContract,
  disabledDirections: { injection: true, offtake: true },
  requiresUpdate: false,
});

/**
 * FT — Customer Switch (admin-only), the PARTIAL-direction decision (plan D1 / D5).
 *
 * One source of truth for "an admin is looking at a single direction that is active/future
 * under ANOTHER company — may it be switched?". Returns
 *   - a `customer-switch` result when the point's service components allow that direction
 *     to be contracted on its own;
 *   - a `customer-switch-blocked` result when the point is `onlyOffinje` (combined-only), so
 *     a single-direction switch is structurally impossible and we refuse rather than guess;
 *   - `null` when this is not an admin/foreign partial case, so the caller keeps its
 *     existing (non-admin / own-company) behaviour byte-for-byte.
 *
 * Deliberately keyed on `deriveServiceCapabilities` (SC_COMMINJ **or** SC_INJECTI, etc.)
 * rather than the narrower `hasAllThreeConfigs` triple.
 */
const resolveAdminPartialSwitch = (
  row: ExistingCompany | undefined,
  direction: "injection" | "offtake",
  currentCompanyId: number,
  isAdmin: boolean,
  caps: ServiceCapabilities,
  t?: (key: string) => string
): ContractValidationResult | null => {
  if (!isAdmin) return null;
  if (!row) return null;
  if (!["active", "future"].includes(row.activeContract)) return null;
  if (row.company_id === currentCompanyId) return null;

  // Combined-only point: a single direction cannot exist on its own.
  if (caps.onlyOffinje) return buildCombinedOnlyBlocked(row, direction, t);

  // Gate on onlyOffinje ALONE — the rule JediMaster set, and the same rule the
  // other-injection / other-offtake admin branches use. Requiring
  // supportsIndependentDirections here additionally made the D5 path silently inert for
  // empty or single-direction configs, so the admin fell through to a hard stop for a
  // shape the feature is supposed to handle.

  return {
    canProceed: true,
    type: "customer-switch",
    customerSwitch: true,
    message: row.companyName,
    directions: [direction],
    companyName: row.companyName,
    companyId: row.company_id,
    activeContract: row.activeContract,
    disabledDirections: { injection: false, offtake: false },
    requiresUpdate: false,
  };
};

/**
 * How many DISTINCT other companies hold an active/future contract on this EAN.
 *
 * Single source of truth, because the two predicates that needed this answer disagreed:
 * this file required an injection row AND an offtake row under different owners, while
 * `resolveCustomerSwitchLegs` counted distinct owners across ALL rows. Two active rows on
 * the SAME direction under two different companies therefore passed validation (the
 * `find()` took the first and it fell through to a normal single-direction switch) while
 * resolveCustomerSwitchLegs returned NO legs — an enabled CTA driving a run with nothing
 * to do, which wrote an orphan SDP and reported success.
 *
 * Counting owners per EAN is the correct rule: if more than one other company holds this
 * point we cannot know whose contract to end, whatever direction the rows are on.
 */
export const countForeignActiveOwners = (
  checkExistingEan: ExistingCompany[] | undefined,
  currentCompanyId: number
): number =>
  new Set(
    (checkExistingEan ?? [])
      .filter(
        (c) =>
          c.company_id !== currentCompanyId &&
          ["active", "future"].includes((c.activeContract ?? "").trim().toLowerCase())
      )
      .map((c) => c.company_id)
  ).size;

const isSwitchable = (row: ExistingCompany, currentCompanyId: number): boolean =>
  row.company_id !== currentCompanyId &&
  ["active", "future"].includes((row.activeContract ?? "").trim().toLowerCase());

/**
 * THE ownership derivation for the Customer Switch decision: the foreign, active/future row
 * on one direction, chosen DETERMINISTICALLY.
 *
 * The bug this replaces: the branches below pick one arbitrary row per direction with
 * `find()`, while every other consumer (resolveCustomerSwitchLegs, the switchType effect,
 * countForeignActiveOwners) scans EVERY row with `.some()`. CheckExistingEan runs with
 * companyFilter:false, its UNION has no ORDER BY, and excludeEnded -> NOT_ENDED keeps
 * `unsigned` rows. So when the landing company has an abandoned `unsigned` row on the SAME
 * direction as the outgoing company's `active` one, `find()` could return either. Row order
 * decided whether the screen said "available for contract" while the switchType effect
 * stamped "Customer Switch" and suppressed the FT-3581 gate — after which the admin walked
 * the NORMAL wizard and that label was written onto a plain contract.
 *
 * Sorted on the row's own values so the answer cannot depend on row order. The
 * multi-owner guard above already guarantees a single foreign owner reaches this point, so
 * company_id is effectively decisive; the rest is a stable tie-break.
 * Scoped to the switch path ONLY: the non-admin branches keep their original `find()`
 * results, so ordinary onboarding is untouched.
 */
const pickForeignSwitchable = (
  rows: ExistingCompany[],
  direction: "injection" | "offtake",
  currentCompanyId: number
): ExistingCompany | undefined => {
  const label = direction === "injection" ? "injectie" : "afname";
  return rows
    .filter((c) => c.direction.toLowerCase() === label && isSwitchable(c, currentCompanyId))
    .sort(
      (a, b) =>
        a.company_id - b.company_id ||
        (a.companyName ?? "").localeCompare(b.companyName ?? "") ||
        (a.activeContract ?? "").localeCompare(b.activeContract ?? "")
    )[0];
};

export const validateExistingEanContracts = (
  checkExistingEan: ExistingCompany[],
  currentCompanyId: number,
  t?: (key: string) => string,
  currentCompanyName?: string,
  sdpAtriasConfig?: string[],
  isAdmin = false,
  serviceComponent?: string
): ContractValidationResult => {
  const hasAllThreeConfigs =
    Array.isArray(sdpAtriasConfig) &&
    ["SC_COMMINJ", "SC_COMMOFF", "SC_OFFINJE"].every((code) => sdpAtriasConfig.some((config) => config.includes(code)));

  // Wider than `hasAllThreeConfigs`: understands SC_COMMINJ **or** SC_INJECTI,
  // SC_COMMOFF **or** SC_OFFTAKE, and SC_OFFINJE. Drives the admin Customer Switch
  // decisions only — the non-admin branches below still key on hasAllThreeConfigs
  // exactly as before.
  // serviceComponent is the documented fallback when sdpAtriasConfig is empty. Omitting it
  // here left that fallback dead on this path while the wizard's own call sites passed it.
  const caps = deriveServiceCapabilities(sdpAtriasConfig, serviceComponent);

  if (!checkExistingEan || checkExistingEan.length === 0) {
    return {
      canProceed: true,
      type: "success",
      message: t ? t("success.meterFound") : "This EAN is available for contract.",
      disabledDirections: { injection: false, offtake: false },
      requiresUpdate: false,
    };
  }

  const injection = checkExistingEan.find((c) => c.direction.toLowerCase() === "injectie");
  const offtake = checkExistingEan.find((c) => c.direction.toLowerCase() === "afname");

  // Switch-path-only, deterministic view of who holds what. Read by the admin block below;
  // the non-admin branches continue to use `injection` / `offtake` above unchanged.
  const switchableInjection = pickForeignSwitchable(checkExistingEan, "injection", currentCompanyId);
  const switchableOfftake = pickForeignSwitchable(checkExistingEan, "offtake", currentCompanyId);

  // S6 / plan D3 / Gate-4 — MORE THAN ONE other company holds this EAN.
  //
  // ⚠️ POSITION IS PART OF THE FIX. A guard that expresses a WHOLE-EAN (or whole-request)
  // refusal belongs ABOVE the per-direction dispatch. Correct logic in the wrong position
  // is dead code: this check previously sat below the hasAllThreeConfigs block, whose
  // partial sub-branches return first, so for that config it never ran at all. The same
  // mistake was made twice in this file — the combined-only stop was also once placed
  // inside a block whose condition made it definitionally unreachable. If you add a guard
  // here, ask which scope it refuses at, and put it at that scope.
  //
  // Counted per EAN via the shared countForeignActiveOwners, NOT as "an injection row and
  // an offtake row with different owners". Two active rows on the SAME direction under two
  // companies fell through that older test into a normal single-direction switch, leaving
  // the CTA enabled while resolveCustomerSwitchLegs returned NO legs.
  // Admin-only: non-admins already hard-stop on other-injection / other-offtake below.
  if (isAdmin && countForeignActiveOwners(checkExistingEan, currentCompanyId) > 1) {
    return {
      canProceed: false,
      type: "customer-switch-blocked",
      message: t
        ? t("management:customerSwitch.errorMultipleOwners")
        : "This connection point has active contracts under more than one other company and can't be switched automatically.",
      directions: ["injection", "offtake"],
      companyName: (injection ?? offtake)?.companyName,
      companyId: (injection ?? offtake)?.company_id,
      activeContract: (injection ?? offtake)?.activeContract,
      disabledDirections: { injection: true, offtake: true },
      requiresUpdate: false,
    };
  }

  // FT — Customer Switch (admin): the AUTHORITATIVE decision, driven by the deterministic
  // derivation above rather than by whichever row `find()` happened to return. Placed here,
  // above the per-direction dispatch, for the same reason the multi-owner guard is: it
  // refuses/permits at EAN scope. Exactly one foreign owner is guaranteed by the guard above.
  if (isAdmin && (switchableInjection || switchableOfftake)) {
    const both = !!switchableInjection && !!switchableOfftake;
    const primary = (switchableInjection ?? switchableOfftake) as ExistingCompany;
    // A combined-only point cannot hand over one direction on its own (§5k revised).
    if (caps.onlyOffinje && !both) {
      return buildCombinedOnlyBlocked(
        primary,
        switchableInjection ? "injection" : "offtake",
        t
      );
    }
    return {
      canProceed: true,
      type: "customer-switch",
      customerSwitch: true,
      message: primary.companyName,
      directions: both
        ? ["injection", "offtake"]
        : switchableInjection
          ? ["injection"]
          : ["offtake"],
      companyName: primary.companyName,
      companyId: primary.company_id,
      activeContract: primary.activeContract,
      disabledDirections: { injection: false, offtake: false },
      requiresUpdate: false,
    };
  }

  //Sepcial handling for SDP Atrias setup with all three configs
  if (hasAllThreeConfigs) {
    // Only injection contract present and active, offtake missing
    if (
      injection &&
      ["active", "future"].includes(injection.activeContract) &&
      (!offtake || !["active", "future"].includes(offtake.activeContract))
    ) {
      // FT — Customer Switch (admin), plan D1: this warning used to return unconditionally
      // and DISABLE the very direction being switched, preempting the admin
      // `other-injection` branch below so the partial case never reached the feature.
      // Admin + foreign now resolves to the Customer Switch (or an explicit stop).
      const adminSwitch = resolveAdminPartialSwitch(injection, "injection", currentCompanyId, isAdmin, caps, t);
      if (adminSwitch) return adminSwitch;

      // Always show warning, even if it's your own company
      return {
        canProceed: true,
        type: "warning",
        message: t
          ? `${t("errors.eanHasActiveContract")} ${injection.activeContract} ${t("services.injection")} ${t(
              "errors.with"
            )} ${injection.companyName}. ${t("warnings.proceedWithOfftake")}`
          : `This EAN already has an Injection (${injection.activeContract}) contract with ${injection.companyName}. You can proceed with Offtake.`,
        disabledDirections: { injection: true, offtake: false },
        requiresUpdate: false,
        warning: true,
        companyName: injection.companyName,
        companyId: injection.company_id,
        directions: ["injection"],
        activeContract: injection.activeContract,
      };
    }
    // Only offtake contract present and active, injection missing
    if (
      offtake &&
      ["active", "future"].includes(offtake.activeContract) &&
      (!injection || !["active", "future"].includes(injection.activeContract))
    ) {
      // FT — Customer Switch (admin), plan D1: mirror of the injection-only sub-branch
      // above. §5j confirms the reverse case is real, so BOTH must be fixed.
      const adminSwitch = resolveAdminPartialSwitch(offtake, "offtake", currentCompanyId, isAdmin, caps, t);
      if (adminSwitch) return adminSwitch;

      // Always show warning, even if it's your own company
      return {
        canProceed: true,
        type: "warning",
        message: t
          ? `${t("errors.eanHasActiveContract")} ${offtake.activeContract} ${t("services.offtake")} ${t(
              "errors.with"
            )} ${offtake.companyName}. ${t("warnings.proceedWithInjection")}`
          : `This EAN already has an Offtake (${offtake.activeContract}) contract with ${offtake.companyName}. You can proceed with Injection.`,
        disabledDirections: { injection: false, offtake: true },
        requiresUpdate: false,
        warning: true,
        companyName: offtake.companyName,
        companyId: offtake.company_id,
        directions: ["offtake"],
        activeContract: offtake.activeContract,
      };
    }
    if (
      injection &&
      offtake &&
      ["active", "future"].includes(injection.activeContract) &&
      ["active", "future"].includes(offtake.activeContract)
    ) {
      // ...fall through to normal logic below...
    }
  }

  // Own company, both directions
  if (
    injection &&
    offtake &&
    injection.company_id === currentCompanyId &&
    offtake.company_id === currentCompanyId &&
    ["active", "future"].includes(injection.activeContract) &&
    ["active", "future"].includes(offtake.activeContract)
  ) {
    return {
      canProceed: false,
      type: "own-both",
      message: t
        ? `${t("errors.eanAlreadyHasContract")} ${t("services.injection")} & ${t("services.offtake")} ${t(
            "errors.withYourCompany"
          )} (${currentCompanyName || ""}). ${t("errors.cannotAddAgain")}`
        : `This EAN already has an Injection & Offtake contract with your company (${
            currentCompanyName || ""
          }). You cannot add it again.`,
      directions: ["injection", "offtake"],
      companyName: currentCompanyName,
      companyId: injection.company_id,
      activeContract: injection.activeContract,
      disabledDirections: { injection: true, offtake: true },
      requiresUpdate: true,
    };
  }

  // Own company, injection only
  if (
    injection &&
    injection.company_id === currentCompanyId &&
    ["active", "future"].includes(injection.activeContract)
  ) {
    // FT — Customer Switch (admin), plan D5/§5p: this branch's condition never checked that
    // the OTHER direction is absent or non-foreign, despite its comment. So when B already
    // holds injection and Company A holds the offtake, the hard-stop below fired and the
    // admin `other-offtake` branch was never reached — blocking a legitimate partial
    // handover. Switch the FOREIGN direction only; B keeps its own injection untouched.
    // (B holding BOTH directions is caught by `own-both` above and still hard-stops.)
    const adminSwitch = resolveAdminPartialSwitch(offtake, "offtake", currentCompanyId, isAdmin, caps, t);
    if (adminSwitch) return adminSwitch;

    return {
      canProceed: false,
      type: "own-injection",
      message: t
        ? `${t("errors.eanAlreadyHasContract")} ${injection.activeContract} ${t("services.injection")} ${t(
            "errors.withYourCompany"
          )} (${currentCompanyName || ""}). ${t("errors.cannotAddAgain")}`
        : `This EAN already has an Injection (${injection.activeContract}) contract with your company (${
            currentCompanyName || ""
          }). You cannot add it again.`,
      directions: ["injection"],
      companyName: currentCompanyName,
      companyId: injection.company_id,
      activeContract: injection.activeContract,
      disabledDirections: { injection: true, offtake: false },
      requiresUpdate: true,
    };
  }

  // Own company, offtake only
  if (offtake && offtake.company_id === currentCompanyId && ["active", "future"].includes(offtake.activeContract)) {
    // FT — Customer Switch (admin), plan D5/§5p: mirror of "own company, injection only".
    // B holds the offtake, Company A holds the injection → switch the injection only.
    const adminSwitch = resolveAdminPartialSwitch(injection, "injection", currentCompanyId, isAdmin, caps, t);
    if (adminSwitch) return adminSwitch;

    return {
      canProceed: false,
      type: "own-offtake",
      message: t
        ? `${t("errors.eanAlreadyHasContract")} ${offtake.activeContract} ${t("services.offtake")} ${t(
            "errors.withYourCompany"
          )} (${currentCompanyName || ""}). ${t("errors.cannotAddAgain")}`
        : `This EAN already has an Offtake (${offtake.activeContract}) contract with your company (${
            currentCompanyName || ""
          }). You cannot add it again.`,
      directions: ["offtake"],
      companyName: currentCompanyName,
      companyId: offtake.company_id,
      activeContract: offtake.activeContract,
      disabledDirections: { injection: false, offtake: true },
      requiresUpdate: true,
    };
  }


  // Other company, both directions
  if (
    injection &&
    offtake &&
    injection.company_id === offtake.company_id &&
    injection.company_id !== currentCompanyId &&
    ["active", "future"].includes(injection.activeContract) &&
    ["active", "future"].includes(offtake.activeContract)
  ) {
    // Customer Switch (admin only): lift the hard-stop and onboard programmatically.
    if (isAdmin) {
      return {
        canProceed: true,
        type: "customer-switch",
        customerSwitch: true,
        message: injection.companyName,
        directions: ["injection", "offtake"],
        companyName: injection.companyName,
        companyId: injection.company_id,
        activeContract: injection.activeContract,
        disabledDirections: { injection: false, offtake: false },
        requiresUpdate: false,
      };
    }
    return {
      canProceed: false,
      type: "other-both",
      message: t
        ? `${t("errors.eanHasActiveContract")} ${t("services.injection")} & ${t("services.offtake")} ${t(
            "errors.with"
          )} ${injection.companyName}. ${t("errors.andCannotProceed")}`
        : `This EAN already has an Injection & Offtake contract with ${injection.companyName}. You cannot add it again.`,
      directions: ["injection", "offtake"],
      companyName: injection.companyName,
      companyId: injection.company_id,
      activeContract: injection.activeContract,
      disabledDirections: { injection: true, offtake: true },
      requiresUpdate: false,
    };
  }

  // Other company, injection only
  if (
    injection &&
    injection.company_id !== currentCompanyId &&
    ["active", "future"].includes(injection.activeContract)
  ) {
    // Customer Switch (admin only): lift the hard-stop and onboard programmatically.
    if (isAdmin) {
      // A combined-only (SC_OFFINJE) point cannot hand over one direction on its own.
      // This is where such a point actually lands — it fails hasAllThreeConfigs, so the
      // partial sub-branches above never see it. Every other config keeps today's
      // behaviour, INCLUDING a single-direction-capability EAN (e.g. SC_OFFTAKE only),
      // which is a legitimate single-direction switch.
      if (caps.onlyOffinje) return buildCombinedOnlyBlocked(injection, "injection", t);
      return {
        canProceed: true,
        type: "customer-switch",
        customerSwitch: true,
        message: injection.companyName,
        directions: ["injection"],
        companyName: injection.companyName,
        companyId: injection.company_id,
        activeContract: injection.activeContract,
        disabledDirections: { injection: false, offtake: false },
        requiresUpdate: false,
      };
    }
    return {
      canProceed: false,
      type: "other-injection",
      message: t
        ? `${t("errors.eanHasActiveContract")} ${injection.activeContract} ${t("services.injection")} ${t(
            "errors.with"
          )} ${injection.companyName} ${t("errors.andCannotProceed")}`
        : `This EAN already has an Injection (${injection.activeContract}) contract with ${injection.companyName}. You cannot add it again.`,
      directions: ["injection"],
      companyName: injection.companyName,
      companyId: injection.company_id,
      activeContract: injection.activeContract,
      disabledDirections: { injection: true, offtake: false },
      requiresUpdate: false,
    };
  }

  // Other company, offtake only
  if (offtake && offtake.company_id !== currentCompanyId && ["active", "future"].includes(offtake.activeContract)) {
    // Customer Switch (admin only): lift the hard-stop and onboard programmatically.
    if (isAdmin) {
      // Mirror of other-injection: combined-only point ⇒ no single-direction handover.
      if (caps.onlyOffinje) return buildCombinedOnlyBlocked(offtake, "offtake", t);
      return {
        canProceed: true,
        type: "customer-switch",
        customerSwitch: true,
        message: offtake.companyName,
        directions: ["offtake"],
        companyName: offtake.companyName,
        companyId: offtake.company_id,
        activeContract: offtake.activeContract,
        disabledDirections: { injection: false, offtake: false },
        requiresUpdate: false,
      };
    }
    return {
      canProceed: false,
      type: "other-offtake",
      message: t
        ? `${t("errors.eanHasActiveContract")} ${offtake.activeContract} ${t("services.offtake")} ${t("errors.with")} ${
            offtake.companyName
          } ${t("errors.andCannotProceed")}`
        : `This EAN already has an Offtake (${offtake.activeContract}) contract with ${offtake.companyName}. You cannot add it again.`,
      directions: ["offtake"],
      companyName: offtake.companyName,
      companyId: offtake.company_id,
      activeContract: offtake.activeContract,
      disabledDirections: { injection: false, offtake: true },
      requiresUpdate: false,
    };
  }

  // Warning: Linked to another company (inactive contracts)
  if (
    (injection &&
      injection.company_id !== currentCompanyId &&
      injection.activeContract !== "active" &&
      injection.activeContract !== "future") ||
    (offtake &&
      offtake.company_id !== currentCompanyId &&
      offtake.activeContract !== "active" &&
      offtake.activeContract !== "future")
  ) {
    const warningCompanyName = injection?.companyName || offtake?.companyName || "";
    return {
      canProceed: true,
      type: "warning",
      message: t
        ? `This EAN is linked to another company  ${warningCompanyName}, it has inactive contract. Proceed with caution.`
        : `This EAN is linked to another company (${warningCompanyName}, inactive contract). Proceed with caution.`,
      directions: [
        ...(injection && injection.company_id !== currentCompanyId ? (["injection"] as const) : []),
        ...(offtake && offtake.company_id !== currentCompanyId ? (["offtake"] as const) : []),
      ],
      companyName: warningCompanyName,
      companyId: injection?.company_id || offtake?.company_id,
      disabledDirections: { injection: false, offtake: false },
      requiresUpdate: false,
      warning: true,
    };
  }

  // Success
  return {
    canProceed: true,
    type: "success",
    message: t ? t("success.meterFound") : "This EAN is available for contract.",
    disabledDirections: { injection: false, offtake: false },
    requiresUpdate: false,
  };
};
// export const validateRunningProcess = (
//   runningProcessModule: string | null,
//   runningProcessRequestorInvolvedBEAssociatedParty: string | null,
//   runningProcessLabel?: string | null
// ) => {
//   if (!runningProcessModule) {
//     return { canProceed: true };
//   }

//   if (runningProcessRequestorInvolvedBEAssociatedParty === "yuso") {
//     return { canProceed: true };
//   }

//   if (runningProcessModule === "Initiate Stop Access" || runningProcessLabel === "Non Residential End Of Contract") {
//     return { canProceed: true };
//   }

//   return {
//     canProceed: false,
//     message: "This EAN has an active process from another party and cannot proceed.",
//   };
// };
export const validateRunningProcess = (
  runningProcessModule: string | null,
  runningProcessRequestorInvolvedBEAssociatedParty: string | null
) => {
  if (!runningProcessModule) {
    return { canProceed: true };
  }

  const blockingProcesses = ["Move-in", "Move in", "Start Access", "Start-Access"];

  const isBlockingProcess = blockingProcesses.some((process) =>
    runningProcessModule.toLowerCase().includes(process.toLowerCase())
  );

  if (isBlockingProcess && runningProcessRequestorInvolvedBEAssociatedParty !== "Yuso") {
    return {
      canProceed: false,
      message: `This EAN has an active ${runningProcessModule} process from another party and cannot proceed.`,
    };
  }

  return { canProceed: true };
};
